import { useMemo } from 'react';
import { Banknote, Package, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { categoryDef } from '@/lib/constants';
import { formatVNDShort } from '@/lib/format';
import type { ReportSegment } from '@/lib/api';

export interface ReportMarketShareProps {
  brandName: string;
  brandTotal: { revenue_vnd: number; units_sold: number };
  marketTotal: { revenue_vnd: number; units_sold: number };
  brandSegments: ReportSegment[];
  marketSegments: Array<{ category: string; units_sold: number; revenue_vnd: number }>;
}

interface SegmentCompareRow {
  category: string;
  label: string;
  brandRevenue: number;
  marketRevenue: number;
  brandUnits: number;
  marketUnits: number;
  shareRevenue: number;
  shareUnits: number;
}

function ShareBar({ pct }: { pct: number }) {
  return (
    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-primary/70"
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
}

function SharePosition({ pct }: { pct: number }) {
  // Threshold màu sắc:
  //  ≥ 40% → mạnh (xanh), 20-40% → ổn (vàng), < 20% → cần đẩy (xám)
  const Icon = pct >= 40 ? TrendingUp : pct < 20 ? TrendingDown : Minus;
  const color = pct >= 40 ? 'text-cat-graphic' : pct < 20 ? 'text-muted-foreground' : 'text-cat-curved';
  return (
    <span className={cn('inline-flex items-center gap-0.5 font-semibold tabular-nums', color)}>
      <Icon className="h-3 w-3" strokeWidth={2.5} />
      {pct.toFixed(1)}%
    </span>
  );
}

export function ReportMarketShare({
  brandName,
  brandTotal,
  marketTotal,
  brandSegments,
  marketSegments,
}: ReportMarketShareProps) {
  const shareRevenue = marketTotal.revenue_vnd > 0 ? (brandTotal.revenue_vnd / marketTotal.revenue_vnd) * 100 : 0;
  const shareUnits = marketTotal.units_sold > 0 ? (brandTotal.units_sold / marketTotal.units_sold) * 100 : 0;

  const segmentRows = useMemo<SegmentCompareRow[]>(() => {
    const marketMap = new Map(marketSegments.map((s) => [s.category, s]));
    const rows: SegmentCompareRow[] = brandSegments
      .map((bs) => {
        const ms = marketMap.get(bs.category);
        const def = categoryDef(bs.category);
        const marketRev = ms?.revenue_vnd ?? 0;
        const marketUnits = ms?.units_sold ?? 0;
        return {
          category: bs.category,
          label: def?.label ?? bs.category,
          brandRevenue: bs.revenue_vnd,
          marketRevenue: marketRev,
          brandUnits: bs.units_sold,
          marketUnits: marketUnits,
          shareRevenue: marketRev > 0 ? (bs.revenue_vnd / marketRev) * 100 : 0,
          shareUnits: marketUnits > 0 ? (bs.units_sold / marketUnits) * 100 : 0,
        };
      })
      .filter((r) => r.brandRevenue > 0 || r.marketRevenue > 0)
      .sort((a, b) => b.marketRevenue - a.marketRevenue);
    return rows;
  }, [brandSegments, marketSegments]);

  return (
    <div className="space-y-4">
      {/* Top: 2 KPI rows so sánh ASUS vs Industry */}
      <div className="grid gap-3 md:grid-cols-2">
        <CompareCard
          icon={Banknote}
          label="Doanh thu"
          brandLabel={brandName}
          brandValue={brandTotal.revenue_vnd}
          marketValue={marketTotal.revenue_vnd}
          formatValue={formatVNDShort}
          sharePct={shareRevenue}
        />
        <CompareCard
          icon={Package}
          label="Số lượng"
          brandLabel={brandName}
          brandValue={brandTotal.units_sold}
          marketValue={marketTotal.units_sold}
          formatValue={(v) => v.toLocaleString('vi-VN')}
          sharePct={shareUnits}
        />
      </div>

      {/* Bottom: per-segment market share table */}
      {segmentRows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full border-collapse text-[11px]">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="px-2 py-2 text-left font-semibold uppercase tracking-wider">Phân khúc</th>
                <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">{brandName}</th>
                <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">Toàn ngành</th>
                <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">% Thị phần</th>
                <th className="hidden px-2 py-2 text-left font-semibold uppercase tracking-wider md:table-cell">
                  Vị thế
                </th>
              </tr>
            </thead>
            <tbody>
              {segmentRows.map((r) => {
                const def = categoryDef(r.category);
                return (
                  <tr key={r.category} className="border-b border-border print-no-break">
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5">
                        {def && <def.icon className={cn('h-3 w-3', def.colorClass)} />}
                        <span className="font-medium">{r.label}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-primary">
                      {formatVNDShort(r.brandRevenue)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                      {formatVNDShort(r.marketRevenue)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <SharePosition pct={r.shareRevenue} />
                    </td>
                    <td className="hidden px-2 py-2 md:table-cell">
                      <ShareBar pct={r.shareRevenue} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">
        % Thị phần = doanh thu của <span className="font-semibold">{brandName}</span> / tổng doanh thu toàn ngành Monitor cùng phân khúc.
        Vị thế: <span className="font-semibold text-cat-graphic">≥ 40%</span> mạnh ·{' '}
        <span className="font-semibold text-cat-curved">20–40%</span> ổn ·{' '}
        <span className="font-semibold text-muted-foreground">&lt; 20%</span> cần đẩy.
      </p>
    </div>
  );
}

function CompareCard({
  icon: Icon,
  label,
  brandLabel,
  brandValue,
  marketValue,
  formatValue,
  sharePct,
}: {
  icon: typeof Banknote;
  label: string;
  brandLabel: string;
  brandValue: number;
  marketValue: number;
  formatValue: (v: number) => string;
  sharePct: number;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <Icon className="h-4 w-4 text-primary" />
      </div>

      <div className="mt-2 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase text-muted-foreground">{brandLabel}</div>
          <div className="mt-0.5 text-xl font-bold tabular-nums leading-none text-primary">
            {formatValue(brandValue)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-muted-foreground">Toàn ngành</div>
          <div className="mt-0.5 text-xl font-bold tabular-nums leading-none">
            {formatValue(marketValue)}
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-1 border-t pt-2">
        <div className="flex items-baseline justify-between gap-2 text-[11px]">
          <span className="text-muted-foreground">Thị phần</span>
          <SharePosition pct={sharePct} />
        </div>
        <ShareBar pct={sharePct} />
      </div>
    </div>
  );
}
